/**
 * CommunityAnalytics CMS
 *
 * Application entrypoint
 */

import './bootstrap';

require('bootstrap-sass');
require('bootstrap/js/dist/tab'); // bootstrap 5 tabs

/**
 * Vue store setup
 * makes store available in this.$store
 */
import { store } from './store';
import { mapState } from 'vuex';

/**
 * Vue axios setup
 */
Vue.prototype.$http = window.axios;

const vuexGlobals = {
    computed: {
        ...mapState({
            user: (state) => state.cms.user,
            client: (state) => state.cms.client,
            project: (state) => state.cms.project,
            echoError: (state) => state.cms.echo_error,
        }),
        $config() {
            return ca.config;
        },
        routeContext() {
            return {
                client: this.client.base_url,
                project: this.project.slug,
            };
        },
    },
};

// Setup store, populate from ca object
store.dispatch('initApp').then(() => {
    // Add top level vue globals
    Vue.mixin(vuexGlobals);
});

// Make store available as $store in vue componets
import storePlugin from './store/plugin';
Vue.use(storePlugin);

/**
 * Add Ziggy routes to Vue
 */
Vue.mixin({
    methods: {
        route: route,
    },
});

/**
 * Vue plugins
 */

// Toast
import Toasted from 'vue-toasted';

const toastedDefaults = {
    iconPack: 'fontawesome',
    position: 'top-center',
    className: 'ca-notification',
};

Vue.use(Toasted, toastedDefaults);

// Tag input
import InputTag from 'vue-input-tag';
Vue.component('input-tag', InputTag);

// Form validation
import vuelidate from 'vuelidate';
import vuelidateErrorExtractor, { templates } from 'vuelidate-error-extractor';

Vue.use(vuelidate);
Vue.use(vuelidateErrorExtractor, {
    template: templates.singleErrorExtractor.bootstrap3, // you can also pass your own custom template
    messages: { required: 'The {attribute} field is required' }, // error messages to use
});

// Tooltips
import VueTippy, { TippyComponent } from 'vue-tippy';

Vue.use(VueTippy, {
    arrow: true,
    arrowType: 'round',
});

Vue.component('tippy', TippyComponent);

// Formulate - https://vueformulate.com/
import VueFormulate from '@braid/vue-formulate';
import FormulateCodeMirrorComponent from '@/components/formulate/FormulateCodeMirror';
import FormulateColorPickerComponent from '@/components/formulate/FormulateColorPicker';
import FormulateIconPickerComponent from '@/components/formulate/FormulateIconPicker';
import FormulateCopyFieldComponent from '@/components/formulate/FormulateCopyField';
import FormulateVueSelectComponent from '@/components/formulate/FormulateVueSelect';
import FormulateLocalizationComponent from '@/components/formulate/FormulateLocalization';
import FormulateTinymceComponent from '@/components/formulate/FormulateTinymce';
Vue.component('FormulateCodeMirrorComponent', FormulateCodeMirrorComponent);
Vue.component('FormulateColorPickerComponent', FormulateColorPickerComponent);
Vue.component('FormulateIconPickerComponent', FormulateIconPickerComponent);
Vue.component('FormulateCopyFieldComponent', FormulateCopyFieldComponent);
Vue.component('FormulateVueSelectComponent', FormulateVueSelectComponent);
Vue.component('FormulateLocalizationComponent', FormulateLocalizationComponent);
Vue.component('FormulateTinymceComponent', FormulateTinymceComponent);

Vue.use(VueFormulate, {
    library: { 
        tinymceField: {
            classification: 'text',
            component: 'FormulateTinymceComponent',
        },
        codemirror: {
            classification: 'text',
            component: 'FormulateCodeMirrorComponent',
            slotProps: {
                component: ['refresh', 'language', 'theme'],
            },
        },
        colorpicker: {
            classification: 'text',
            component: 'FormulateColorPickerComponent',
            slotProps: {
                component: ['mode', 'shape', 'position'],
            },
        },
        iconpicker: {
            classification: 'text',
            component: 'FormulateIconPickerComponent',
        },
        copyfield: {
            classification: 'text',
            component: 'FormulateCopyFieldComponent',
            slotProps: {
                component: ['icon', 'qrcode'],
            },
        },
        'vue-select': {
            classification: 'select',
            component: 'FormulateVueSelectComponent',
            slotProps: {
                component: ['useColours', 'fetchOptions'],
            },
        },
        localization: {
            classification: 'select',
            component: 'FormulateLocalizationComponent',
        },
    },
});

/**
 * Global vue components
 */

import LoginModal from '@/components/login/LoginModal';
Vue.component('login-modal', LoginModal);

import MediaSelectModal from '@/components/media/modals/media-select-modal';
Vue.component('media-select-modal', MediaSelectModal);

import MediaUploadModal from '@/components/media/modals/UploadModal';
Vue.component('media-upload-modal', MediaUploadModal);

import UserManageModal from '@/components/user/modal';
Vue.component('user-modal', UserManageModal);

import BaseQrCodeButton from '@/components/base/BaseQrCodeButton';
Vue.component('qr-code-btn', BaseQrCodeButton);

import CopyField from '@/components/copy-field';
Vue.component('copy-field', CopyField);

import AlertMessage from '@/components/alert-message';
Vue.component('alert-message', AlertMessage);

/**
 * Global vue directives
 */
import VueClipboard from '@/components/directives/clipboard';
Vue.use(VueClipboard);

import '@/components/directives/role';

/**
 * Global vue mixins
 */
import downloadQRCode from '@/components/mixins/downloadQRCode';

/*
 * Localisation
 */
import { i18n } from '@/i18n';

// Add Vue isntance if we dont alreday have one
document.addEventListener('DOMContentLoaded', () => {
    if (document.getElementById('app') !== null && typeof window.App === 'undefined') {
        window.App = new Vue({
            el: '#app',
            name: 'App',
            i18n,
            mixins: [downloadQRCode],
        });
    }
});

if (window.ca.loginExpired) {
    store.dispatch('showLoginModal');
}

import './media';
import './echo';
import './export';

import * as Charts from './charts';
window.ca.charts = Charts;

import './flot';
import './tooltip';
import Vue from 'vue';
require('./event-bus');
